import * as React from 'react';
import qs from 'qs';
import { flattenDeep, isEmpty, map } from 'lodash';

import endpoints from 'src/common/config/endpoints';
import { useGet, IFetchResult } from 'src/utils/hooks/useGet';

import { useInviteContext } from './useInviteContext';
import { ISocialAccount } from 'src/common/models/socialAccount';
import { ICommunity } from 'src/common/models/community';

const { useMemo, useState } = React;

interface IProps {
  socialAccounts: ISocialAccount[];
}

export const useGroupsForSocialAccounts = (props: IProps) => {
  const { socialAccounts } = props;
  
  const [isAdding, setIsAdding] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [targetGroupId, setTargetGroupId] = useState(0);

  const {
    apiEndpoint,
    clientId,
  } = useInviteContext();

  const hasSocialAccounts = !isEmpty(socialAccounts);

  const { network_identifier } = hasSocialAccounts
    ? socialAccounts[0]
    : { network_identifier: null};

  const socialAccountIds = useMemo(() => (
    map(socialAccounts, (socialAccount) => socialAccount.id)
  ), [socialAccounts]);

  const names = useMemo(() => (
    map(socialAccounts, (socialAccount) => socialAccount.full_display_name)
  ), [socialAccounts]);

  /**
   * Get invited groups
   */
  const fetchGroupsUrl = useMemo(() => {
    if (!apiEndpoint || !clientId) {
      return;
    }

    const params = {
      client_id: clientId,
      account_ids: socialAccountIds,
      network_type: network_identifier,
    };

    const urlParams = qs.stringify(params, { arrayFormat: 'brackets' });

    return `${apiEndpoint}/${endpoints.communitiesForSocialAccounts}?${urlParams}`;
  }, [socialAccounts, apiEndpoint, clientId]);

  const {
    loading: isFetching,
    data: fetchedGroups,
    error,
    refetch,
  } = useGet({ url: fetchGroupsUrl, skip: !hasSocialAccounts });

  const groups: ICommunity[] = useMemo(() => (
    flattenDeep(fetchedGroups)
  ), [fetchedGroups]);

  const addToGroup = async (communityId) => {
    if (!apiEndpoint || !clientId) {
      return;
    }

    const params = {
      client_id: clientId,
      account_ids: socialAccountIds,
      names,
      community_id: communityId,
      network_type: network_identifier,
    };
    const urlParams = qs.stringify(params, { arrayFormat: 'brackets' });
    const url = `${apiEndpoint}/${endpoints.memberAddToCommunity}?${urlParams}`;

    const abortController = new AbortController();
    setIsAdding(true);
    setTargetGroupId(communityId);

    const response = await fetch(url, {
      method: 'POST',
      signal: abortController.signal,
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    });

    const json = await response.json() as IFetchResult<any>;

    setIsAdding(false);
    setTargetGroupId(0);
    refetch();

    return json;
  };

  const removeFromGroup = async (communityId) => {
    if (!apiEndpoint || !clientId) {
      return;
    }

    const params = {
      client_id: clientId,
      account_ids: socialAccountIds,
      names,
      community_id: communityId,
      network_type: network_identifier,
    };
    const urlParams = qs.stringify(params, { arrayFormat: 'brackets' });
    const url = `${apiEndpoint}/${endpoints.memberRemoveFromCommunity}?${urlParams}`;

    const abortController = new AbortController();
    setIsRemoving(true);
    setTargetGroupId(communityId);

    const response = await fetch(url, {
      method: 'POST',
      signal: abortController.signal,
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    });

    const json = await response.json() as IFetchResult<any>;

    setIsRemoving(false);
    setTargetGroupId(0);
    refetch();

    return json;
  };

  return {
    groups: Object.freeze(groups),
    isFetching,
    error,
    addToGroup,
    isAdding,
    removeFromGroup,
    targetGroupId,
    isRemoving,
  };
};
